/**
 * Deterministic UUID (P106).
 *
 * Some rows are created OFFLINE by several devices at once and must converge on
 * the SAME primary key (offline attendance and devotional tracking). A random
 * `generateId()` cannot do that: two offline devices would mint two different
 * v7 ids for the same logical row. This helper derives a stable UUID from the
 * logical key (for example `session:person`), so every device computes the same
 * `uuid` value without needing to coordinate.
 *
 * It is a plain two-pass FNV-1a 64-bit hash set into RFC 4122 shape (version 4
 * bits, variant bits). It is NOT cryptographic and must only be used for ids
 * that are already unique by construction (a pair of ids).
 */

const FNV_OFFSET = 0xcb_f2_9c_e4_84_22_23_25n;
const FNV_PRIME = 0x1_00_00_00_01_b3n;
const MASK64 = 0xff_ff_ff_ff_ff_ff_ff_ffn;
const SECOND_SEED_XOR = 0x9e_37_79_b9_7f_4a_7c_15n;

function fnv1a64(input: string, seed: bigint): bigint {
  let hash = seed;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * FNV_PRIME) & MASK64;
  }
  return hash;
}

/** Stable UUID string for `value`; same input always yields the same UUID. */
export function deterministicUuid(value: string): string {
  const hi = fnv1a64(value, FNV_OFFSET);
  const lo = fnv1a64(value, FNV_OFFSET ^ SECOND_SEED_XOR);
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 8; i += 1) {
    bytes[i] = Number((hi >> BigInt(8 * (7 - i))) & 0xffn);
    bytes[8 + i] = Number((lo >> BigInt(8 * (7 - i))) & 0xffn);
  }
  const b6 = bytes[6] ?? 0;
  const b8 = bytes[8] ?? 0;
  bytes[6] = (b6 & 0x0f) | 0x40; // version 4 (opaque to the DB)
  bytes[8] = (b8 & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
